import React from 'react'
import PropTypes from 'prop-types'
import {AspectRatio, Box, Button, Flex, Img, Link, Text, useStyleConfig} from '@chakra-ui/react'
import {remCalc} from '../../../../theme/_utils'
import useDamImage from '../../../../custom-hooks/use-dam-image'
import {useGetCategoriesData} from '../../../../custom-api/hooks/use-get-categories-data'
import {getGlobalLink, safeBuildUrl} from '../../../../utils/contentstack-utils'
import SanitizedHtml from '../../../html-sanitizer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

export const ContentSliderTile = ({image = null, textblock = null, textVariant, ctaVariant}) => {
    const styles = useStyleConfig('ContentSlider')
    const damImage = useDamImage(image)
    const textColor = {base: textVariant, xl: 'text.inverse'}
    const ctaColor = {base: ctaVariant, xl: 'inverse-primary'}
    const categoryData = useGetCategoriesData()
    const {url, label, isAbsolute} = getGlobalLink(textblock?.button, categoryData)
    const {buildUrl} = useMultiSite()
    const buttonProps = isAbsolute
        ? {
              as: 'a',
              href: url,
              target: '_blank',
              rel: 'noopener noreferrer',
          }
        : {
              as: Link,
              to: safeBuildUrl(url, buildUrl),
          }

    return (
        <>
            <Box data-testid="content-slider-tile-desktop" display={{base: 'none', xl: 'block'}}>
                {damImage.url && (
                    <AspectRatio ratio={damImage.ratio} {...styles.image}>
                        <Img src={damImage.url} alt={image?.alt || ''} color={textVariant} />
                    </AspectRatio>
                )}
                <Flex
                    {...styles.textblock}
                    direction="column"
                    px={`min(12%, ${remCalc(219)})`}
                    backgroundColor="rgba(0, 0, 0, 0.3)"
                    position="absolute"
                    gap={remCalc(40)}
                >
                    <Flex direction="column" gap={remCalc(24)}>
                        {textblock?.label_paragraph && (
                            <Text
                                textStyle="body-xxsmall"
                                fontWeight={'bold'}
                                color={textColor}
                                {...styles.label_paragraph}
                            >
                                {textblock?.label_paragraph}
                            </Text>
                        )}
                        {textblock?.title && (
                            <Text textStyle="display-large" fontWeight={'medium'} color={textColor}>
                                {textblock?.title}
                            </Text>
                        )}
                    </Flex>
                    {textblock?.body_copy && (
                        <SanitizedHtml
                            as={Box}
                            html={textblock?.body_copy}
                            textStyle="body-xlarge"
                            fontWeight={'normal'}
                            color={textColor}
                        />
                    )}
                    {label && url && (
                        <Button {...buttonProps} {...styles.cta} variant={ctaColor}>
                            {label}
                        </Button>
                    )}
                </Flex>
            </Box>

            <Flex
                data-testid="content-slider-tile-mobile"
                display={{base: 'flex', xl: 'none'}}
                direction="column"
                gap={remCalc(24)}
            >
                {damImage.url && (
                    <AspectRatio ratio={damImage.ratio} {...styles.image}>
                        <Img src={damImage.url} alt={image?.alt || ''} color={textVariant} />
                    </AspectRatio>
                )}
                <Flex direction="column" gap={remCalc(24)} alignItems={'center'}>
                    {textblock?.label_paragraph && (
                        <Text
                            textStyle="body-xxsmall"
                            fontWeight={'bold'}
                            color={textColor}
                            {...styles.label_paragraph}
                        >
                            {textblock?.label_paragraph}
                        </Text>
                    )}
                    {textblock?.title && (
                        <Text textStyle="display-large" fontWeight={'medium'} color={textColor}>
                            {textblock?.title}
                        </Text>
                    )}
                    {textblock?.body_copy && (
                        <SanitizedHtml
                            as={Box}
                            html={textblock?.body_copy}
                            textStyle="body-large"
                            fontWeight={'normal'}
                            color={textColor}
                        />
                    )}
                    {label && url && (
                        <Button {...buttonProps} {...styles.cta} variant={ctaColor}>
                            {label}
                        </Button>
                    )}
                </Flex>
            </Flex>
        </>
    )
}

ContentSliderTile.propTypes = {
    image: PropTypes.shape(),
    textblock: PropTypes.shape({
        label: PropTypes.string,
        label_paragraph: PropTypes.string,
        title: PropTypes.string,
        body_copy: PropTypes.string,
        button: PropTypes.shape(),
    }),
    textVariant: PropTypes.string,
    ctaVariant: PropTypes.string,
}
